import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  IconButton,
  Box,
  TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation } from '@apollo/client';
import { gql } from '@apollo/client';

const ADD_DEVICE_MUTATION = gql`
  mutation AddDevice($imei: String!, $serialNumber: String!, $tcuMake: String!, $tcuModel: String!) {
    addDevice(imei: $imei, serial_number: $serialNumber, tcu_make: $tcuMake, tcu_model: $tcuModel) {
      tcu_id
      imei
      serial_number
      tcu_make
      tcu_model
      created_on
    }
  }
`;

interface DeviceDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: () => void;
  onDeviceAdded?: (deviceData: any) => void;
}

const DeviceDialog: React.FC<DeviceDialogProps> = ({ open, onClose, onSubmit, onDeviceAdded }) => {
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ open: false, message: '', severity: 'success' });
  const [serialNo, setSerialNo] = useState('');
  const [imei, setImei] = useState('');
  const [deviceMake, setDeviceMake] = useState('');
  const [deviceModel, setDeviceModel] = useState('');
  const [errors, setErrors] = useState({
    serialNo: '',
    imei: ''
  });

  const [duplicateError, setDuplicateError] = useState('');
  const [addDevice, { loading, error }] = useMutation(ADD_DEVICE_MUTATION, {
    onCompleted: (data) => {
      setDuplicateError('');
      setSnackbar({ open: true, message: 'Device added successfully!', severity: 'success' });
      if (onDeviceAdded) onDeviceAdded(data.addDevice);
      if (onSubmit) onSubmit();
      resetForm();
      setTimeout(() => {
        onClose();
        setSnackbar({ open: false, message: '', severity: 'success' });
      }, 2000);
    },
    onError: (error) => {
      // Check for duplicate key error
      if (error.message.includes('duplicate key value violates unique constraint')) {
        setDuplicateError('A device with the same IMEI or Serial No already exists.');
      } else {
        setDuplicateError('Failed to add device: ' + error.message);
      }
      console.error("Error adding device:", error.message);
    }
  });

  const validateInputs = () => {
    const newErrors = {
      serialNo: '',
      imei: ''
    };
    let isValid = true;

    // Validate IMEI (10-15 characters)
    if (imei.length < 10 || imei.length > 15) {
      newErrors.imei = 'IMEI must be between 10-15 characters';
      isValid = false;
    }

    // Validate Serial No (15-30 characters)
    if (serialNo.length < 15 || serialNo.length > 30) {
      newErrors.serialNo = 'Serial No must be between 15-30 characters';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const resetForm = () => {
    setSerialNo('');
    setImei('');
    setDeviceMake('');
    setDeviceModel('');
    setErrors({ serialNo: '', imei: '' });
  };

  const handleSubmit = () => {
    if (validateInputs()) {
      addDevice({
        variables: {
          imei: imei,
          serialNumber: serialNo,
          tcuMake: deviceMake,
          tcuModel: deviceModel
        }
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ position: 'relative', pr: 5 }}>
        Add New Device
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: 'inherit', position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box mt={2} mb={2}>
          <Grid container spacing={2}>
            {/* First row with two fields */}
            <Grid sx={{xs:12, sm:6 ,width:'100%'}}>
              <TextField
                label="Device Serial No"
                value={serialNo}
                onChange={(e) =>
                  setSerialNo(e.target.value.replace(/[^a-zA-Z0-9]/g, ''))
                }
                fullWidth
                required
                error={!!errors.serialNo}
                helperText={errors.serialNo}
                inputProps={{
                  minLength: 15,
                  maxLength: 30,
                  onInput: (e: any) => {
                    e.target.value = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                  },
                }}
              />
            </Grid>

            <Grid sx={{xs:12, sm:6,width:'100%'}}>
              <TextField
                label="IMEI"
                value={imei}
                onChange={(e) =>
                  setImei(e.target.value.replace(/[^a-zA-Z0-9]/g, ''))
                }
                fullWidth
                required
                error={!!errors.imei}
                helperText={errors.imei}
                inputProps={{
                  minLength: 10,
                  maxLength: 15,
                  onInput: (e: any) => {
                    e.target.value = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                  },
                }}
              />
            </Grid>

            {/* Second row with two fields */}
            <Grid sx={{xs:12, sm:6 ,width:'100%'}}>
              <TextField
                label="Device Make"
                value={deviceMake}
                onChange={(e) =>
                  setDeviceMake(e.target.value.replace(/[^a-zA-Z0-9]/g, ''))
                }
                inputProps={{
                  maxLength: 10,
                  onInput: (e: any) => {
                    e.target.value = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                  },
                }}
                fullWidth
                required
              />
            </Grid>

            <Grid sx={{xs:12, sm:6 ,width:'100%'}}>
              <TextField
                label="Device Model"
                value={deviceModel}
                onChange={(e) =>
                  setDeviceModel(e.target.value.replace(/[^a-zA-Z0-9]/g, ''))
                }
                inputProps={{
                  maxLength: 10,
                  onInput: (e: any) => {
                    e.target.value = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                  },
                }}
                fullWidth
                required
              />
            </Grid>
          </Grid>

          {duplicateError && (
            <Box mt={2} color="error.main">
              {duplicateError}
            </Box>
          )}
          {error && !duplicateError && (
            <Box mt={2} color="error.main">
              Error: {error.message}
            </Box>
          )}
        </Box>
      </DialogContent>


      <DialogActions sx={{ pb: '16px' }}>
        <Button onClick={onClose} variant="outlined" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!serialNo || !imei || !deviceMake || !deviceModel || loading}
          sx={{
            backgroundColor: '#E60012',
            '&:hover': { backgroundColor: '#cc0010' },
          }}
        >
          {loading ? 'Adding...' : 'Add Device'}
        </Button>
      </DialogActions>
      {/* Success Snackbar */}
      <Box>
        {snackbar.open && (
          <div style={{
            position: 'fixed',
            bottom: 24,
            left: '5%',
            transform: 'translateX(-50%)',
            backgroundColor: '#43a047',
            color: '#fff',
            padding: '8px 24px',
            borderRadius: 6,
            zIndex: 9999,
            fontWeight: 500,
            fontSize: '1rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}>
            {snackbar.message}
          </div>
        )}
      </Box>
    </Dialog>
  );
};

export default DeviceDialog;